import React, { useState } from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownFooter,
  DropdownBody,
} from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Select } from "~/react/ui/select"
import { Radio } from "~/react/ui/radio"
import type { Dispatch, SetStateAction } from "react"
import { Company } from "@/types/company"

interface CompanySaveManagerProps {
  id: string
  companyLists: Company[]
  setCompanyLists: Dispatch<SetStateAction<Company[]>>
  onSave: (name: string, listId: number | null) => Promise<void>
  disableButtons: boolean
  children: React.ReactNode
  count: number
}

const createCompanyList = async (name: string): Promise<Company | null> => {
  const response = await fetch("/lead/company-list", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token":
        document.querySelector("meta[name='csrf-token']")?.content || "",
    },
    body: JSON.stringify({
      lead_company_list: { name },
    }),
    redirect: "manual", // Don't follow redirects automatically
  })

  if (!response.ok && response.status !== 302) {
    try {
      const responseData = await response.json()
      App.displayFlash(
        "error",
        responseData.errors?.[0] ||
          i18n.t(
            "js.discover.company_save_manager.errors.failed_to_create_list",
          ),
      )
    } catch {
      App.displayFlash(
        "error",
        i18n.t("js.discover.company_save_manager.errors.failed_to_create_list"),
      )
    }
    return null
  }

  // Handle redirect response (302)
  if (response.status === 302 || response.status === 301) {
    const location = response.headers.get("location")
    if (location) {
      // Extract ID from redirect URL like /leads/company-lists/123
      const match = location.match(/company-lists\/(\d+)/)
      if (match) {
        return { id: parseInt(match[1], 10), name }
      }
    }
  }

  // Try to parse JSON response if available
  try {
    const contentType = response.headers.get("content-type")
    if (contentType?.includes("application/json")) {
      const json = await response.json()
      return json.data || { id: json.id, name: json.name }
    }
  } catch (error) {
    console.error("Error parsing company list creation response:", error)
  }

  App.displayFlash(
    "error",
    i18n.t("js.discover.company_save_manager.errors.failed_to_parse_response"),
  )
  return null
}

export const CompanySaveManager: React.FC<CompanySaveManagerProps> = ({
  id,
  companyLists,
  setCompanyLists,
  onSave,
  children,
  disableButtons,
  count,
}) => {
  const [listType, setListType] = useState<"existing" | "new">(
    companyLists.length > 0 ? "existing" : "new",
  )
  const [listName, setListName] = useState<string>("")
  const [listNameError, setListNameError] = useState<string | null>(null)
  const [selectedListId, setSelectedListId] = useState<number | null>(
    companyLists[0]?.id || null,
  )
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    setListNameError(null)

    try {
      if (listType === "new") {
        if (!listName.trim()) {
          setListNameError(
            i18n.t(
              "js.discover.company_save_manager.errors.list_name_required",
            ),
          )
          setIsSaving(false)
          return
        }

        const newList = await createCompanyList(listName.trim())
        if (!newList) {
          setIsSaving(false)
          return
        }

        setCompanyLists((prev) => [...prev, newList])
        await onSave(newList.name, newList.id)
      } else {
        if (!selectedListId) {
          setListNameError(
            i18n.t(
              "js.discover.company_save_manager.errors.select_list_required",
            ),
          )
          setIsSaving(false)
          return
        }

        const selectedList = companyLists.find(
          (list) => list.id === selectedListId,
        )
        if (selectedList) {
          await onSave(selectedList.name, selectedList.id)
        }
      }
    } catch (error) {
      console.error("Error saving companies:", error)
      App.displayFlash(
        "error",
        i18n.t(
          "js.discover.company_save_manager.errors.failed_to_save_companies",
        ),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const selectList = ({ value }: { value: number }) => {
    const companyList = companyLists.find((list) => list.id === value)
    if (companyList) {
      setListName("")
      setListNameError(null)
      setSelectedListId(companyList.id)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="company-save-manager overflow-visible">
        <DropdownBody>
          <div className="h-form-group mb-4">
            <div className="h-form-group__input">
              <Radio
                id={`company-${id}-save-manager-existing-lists`}
                name={`company-${id}-save-manager-list-type`}
                checked={listType === "existing"}
                onChange={() => setListType("existing")}
              />
            </div>
            <div className="h-form-group__infos">
              <label
                className="h-label h-label--sm"
                htmlFor={`company-${id}-save-manager-existing-lists`}
              >
                {i18n.t(
                  "js.discover.company_save_manager.save_to_existing_list",
                )}
              </label>
              {listType === "existing" && (
                <>
                  <Select
                    options={companyLists.map((list) => ({
                      value: list.id,
                      label: list.name,
                    }))}
                    placeholder={i18n.t(
                      "js.discover.company_save_manager.select_list_placeholder",
                    )}
                    isClearable={false}
                    isMulti={false}
                    onChange={selectList}
                    defaultValue={selectedListId}
                  />
                </>
              )}
            </div>
          </div>

          <div className="h-form-group">
            <div className="h-form-group__input">
              <Radio
                id={`company-${id}-save-manager-new-list`}
                name={`company-${id}-save-manager-list-type`}
                checked={listType === "new"}
                onChange={() => setListType("new")}
              />
            </div>
            <div className="h-form-group__infos">
              <label
                className="h-label h-label--sm"
                htmlFor={`company-${id}-save-manager-new-list`}
              >
                {i18n.t("js.discover.company_save_manager.create_new_list")}
              </label>
              {listType === "new" && (
                <>
                  <input
                    className="h-input h-input--sm h-input--block"
                    type="text"
                    placeholder={i18n.t(
                      "js.discover.company_save_manager.list_name_placeholder",
                    )}
                    value={listName}
                    onChange={(e) => {
                      setListName(e.target.value)
                      setListNameError(null)
                    }}
                  />
                  {listNameError && (
                    <div className="h-hint h-hint--error mt-2">
                      <span
                        className="far fa-circle-exclamation"
                        aria-hidden="true"
                      ></span>
                      {listNameError}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DropdownBody>

        <DropdownFooter>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={disableButtons || isSaving}
          >
            {isSaving
              ? i18n.t("js.discover.company_save_manager.buttons.saving")
              : i18n.t(
                  "js.discover.company_save_manager.buttons.save_companies",
                  {
                    count,
                  },
                )}
          </Button>
          <Button variant="ghost" size="sm" disabled={disableButtons}>
            {i18n.t("js.discover.company_save_manager.buttons.cancel")}
          </Button>
        </DropdownFooter>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
